/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * ConnectCatalog.jsx — super-admin CRUD UI for Smart Invite catalog
 * (offerings + insights). Styled to match UserManagement:
 *   AdminLayout header + Back + Footer, cc-* classed card + table +
 *   badges + modal + empty-state, Lucide icons.
 *
 * Vocabulary (for_entity_types + for_domains + sections) comes from
 * GET /api/admin/connect/catalog/options so the UI can NEVER drift from
 * the DB CHECK constraints or the connect_contacts enum values.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Mail, Plus, Edit2, Trash2, X, AlertTriangle, CheckCircle, Link as LinkIcon
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import './ConnectCatalog.css';
// R13 Step 4 P1 -- the ConnectEditor merge. Email templates + Invite
// prompts + Status wording + Pilot programmes + CTAs + Campaigns +
// Locked panel. Each tab uses the SAME adminFetch so auth is identical.
import {
  EmailTemplatesTab, EmailCatalogTab, InvitePromptsTab, StatusWordingTab,
  PilotProgrammesTab, CtasTab, CampaignsTab, LockedPanel,
  // R13 Step 7 (task 5) -- Page text tab writes connect_pitch_content.
  PageTextTab
} from './connect/ConnectConfigTabs';

const ADMIN_API_BASE =
  process.env.REACT_APP_ADMIN_API_BASE_URL
  || (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
      ? 'https://skilldelivery-platform.onrender.com'
      : 'http://localhost:5000');

async function adminFetch(path, options = {}) {
  const token = localStorage.getItem('adminAccessToken');
  const twoFASession = localStorage.getItem('admin2FASession');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(twoFASession ? { 'x-2fa-session': twoFASession } : {}),
    ...(options.headers || {})
  };
  const res = await fetch(`${ADMIN_API_BASE}${path}`, { ...options, headers });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.code || null;
    err.body = body;
    throw err;
  }
  return body;
}

// ─── Validation ────────────────────────────────────────────────────────────

const URL_RE = /^https?:\/\/\S+$/i;

function validateOffering(f) {
  const errs = {};
  if (!f.name || !f.name.trim()) errs.name = 'Required.';
  else if (f.name.length > 120) errs.name = 'Max 120 characters.';
  if (!f.one_liner || !f.one_liner.trim()) errs.one_liner = 'Required.';
  else if (f.one_liner.length > 200) errs.one_liner = 'Max 200 characters.';
  if (!f.link_url || !f.link_url.trim()) errs.link_url = 'Required.';
  else if (!URL_RE.test(f.link_url.trim())) errs.link_url = 'Must start with http:// or https://';
  if (!f.section) errs.section = 'Required.';
  return errs;
}
function validateInsight(f) {
  const errs = {};
  if (!f.title || !f.title.trim()) errs.title = 'Required.';
  else if (f.title.length > 200) errs.title = 'Max 200 characters.';
  if (!f.one_liner || !f.one_liner.trim()) errs.one_liner = 'Required.';
  else if (f.one_liner.length > 200) errs.one_liner = 'Max 200 characters.';
  if (!f.link_url || !f.link_url.trim()) errs.link_url = 'Required.';
  else if (!URL_RE.test(f.link_url.trim())) errs.link_url = 'Must start with http:// or https://';
  if (!f.kind) errs.kind = 'Required.';
  return errs;
}

// ═══════════════════════════════════════════════════════════════════════════
// Root
// ═══════════════════════════════════════════════════════════════════════════

export default function ConnectCatalog() {
  const [tab, setTab] = useState('offerings');
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [options, setOptions] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminFetch('/api/admin/connect/catalog/options');
        setOptions(r.data);
      } catch (err) {
        setError(`Could not load catalog options: ${err.message}`);
      }
    })();
  }, []);

  // Auto-clear success toast after 3s.
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(t);
  }, [success]);

  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div className="connect-catalog">
        <div className="cc-header">
          <div className="cc-header-left">
            <Mail className="cc-header-icon" />
            <div>
              <h1>Connect Catalog</h1>
              <p>Offerings and insight cards used in Smart Invite</p>
            </div>
          </div>
        </div>

        {disabled && (
          <div className="cc-message warn">
            <AlertTriangle className="w-4 h-4" />
            Smart Invite is currently <strong>disabled</strong> on this environment
            (SMART_INVITE_ENABLED=false). You can still browse and edit the catalog
            here; owners won&apos;t be able to send invites until Ops flips the switch.
          </div>
        )}
        {error && (
          <div className="cc-message error">
            <AlertTriangle className="w-4 h-4" />
            {error}
            <button onClick={() => setError(null)} aria-label="Dismiss">&times;</button>
          </div>
        )}
        {success && (
          <div className="cc-message success">
            <CheckCircle className="w-4 h-4" />
            {success}
          </div>
        )}

        <div className="cc-tabs">
          <button className={`cc-tab ${tab === 'offerings' ? 'active' : ''}`}
                  onClick={() => setTab('offerings')}>Offerings</button>
          <button className={`cc-tab ${tab === 'insights' ? 'active' : ''}`}
                  onClick={() => setTab('insights')}>Insights &amp; Research</button>
          {/* R13 Step 4 P1 -- merged ConnectEditor tabs. */}
          <button className={`cc-tab ${tab === 'email' ? 'active' : ''}`}
                  onClick={() => setTab('email')}>Email templates</button>
          {/* R15 next -- catalog v2 (connect_email_templates). */}
          <button className={`cc-tab ${tab === 'email_catalog' ? 'active' : ''}`}
                  onClick={() => setTab('email_catalog')}>Email catalog (v2)</button>
          <button className={`cc-tab ${tab === 'prompts' ? 'active' : ''}`}
                  onClick={() => setTab('prompts')}>Invite prompts</button>
          {/* R13 Step 4 P3 -- new pitch-content tabs. */}
          <button className={`cc-tab ${tab === 'wording' ? 'active' : ''}`}
                  onClick={() => setTab('wording')}>Status wording</button>
          <button className={`cc-tab ${tab === 'pilots' ? 'active' : ''}`}
                  onClick={() => setTab('pilots')}>Pilot programmes</button>
          <button className={`cc-tab ${tab === 'ctas' ? 'active' : ''}`}
                  onClick={() => setTab('ctas')}>CTAs</button>
          <button className={`cc-tab ${tab === 'campaigns' ? 'active' : ''}`}
                  onClick={() => setTab('campaigns')}>Campaigns</button>
          {/* R13 Step 7 (task 5) -- invite welcome page labels (064). */}
          <button className={`cc-tab ${tab === 'page_text' ? 'active' : ''}`}
                  onClick={() => setTab('page_text')}>Page text</button>
          <button className={`cc-tab ${tab === 'locked' ? 'active' : ''}`}
                  onClick={() => setTab('locked')}>Locked</button>
        </div>

        {!options ? (
          <div className="cc-card">
            <div className="cc-loading">Loading options…</div>
          </div>
        ) : tab === 'offerings' ? (
          <OfferingsPanel options={options} setDisabled={setDisabled}
                          setError={setError} setSuccess={setSuccess} />
        ) : tab === 'insights' ? (
          <InsightsPanel options={options} setDisabled={setDisabled}
                         setError={setError} setSuccess={setSuccess} />
        ) : tab === 'email' ? (
          <EmailTemplatesTab adminFetch={adminFetch} />
        ) : tab === 'email_catalog' ? (
          <EmailCatalogTab adminFetch={adminFetch} />
        ) : tab === 'prompts' ? (
          <InvitePromptsTab adminFetch={adminFetch} />
        ) : tab === 'wording' ? (
          <StatusWordingTab adminFetch={adminFetch} />
        ) : tab === 'pilots' ? (
          <PilotProgrammesTab adminFetch={adminFetch} options={options} />
        ) : tab === 'ctas' ? (
          <CtasTab adminFetch={adminFetch} options={options} />
        ) : tab === 'campaigns' ? (
          <CampaignsTab adminFetch={adminFetch} options={options} />
        ) : tab === 'page_text' ? (
          <PageTextTab adminFetch={adminFetch} />
        ) : (
          <LockedPanel />
        )}
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Offerings
// ═══════════════════════════════════════════════════════════════════════════

function OfferingsPanel({ options, setDisabled, setError, setSuccess }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch('/api/admin/connect/catalog/offerings');
      setRows(r.data || []);
      setDisabled(false);
    } catch (err) {
      if (err.code === 'SMART_INVITE_DISABLED') { setDisabled(true); setRows([]); }
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [setDisabled, setError]);

  useEffect(() => { load(); }, [load]);

  const sectionLabel = (v) => options.sections.find(s => s.value === v)?.label || v;
  const domainLabel  = (v) => options.domains.find(d => d.value === v)?.label || v;
  const represLabel  = (v) => options.represents.find(r => r.value === v)?.label || v;

  async function remove(row) {
    if (!window.confirm(`Delete "${row.name}"? This cannot be undone.`)) return;
    try {
      await adminFetch(`/api/admin/connect/catalog/offerings/${row.id}`, { method: 'DELETE' });
      setSuccess(`Deleted "${row.name}".`);
      await load();
    } catch (err) { setError(err.message); }
  }

  return (
    <div className="cc-card">
      <div className="cc-card-header">
        <div className="cc-card-title">
          Offerings <span className="cc-count">({rows.length})</span>
        </div>
        <button className="cc-btn primary" onClick={() => setEditing({})}>
          <Plus className="w-4 h-4" /> Add offering
        </button>
      </div>

      {loading ? (
        <div className="cc-loading">Loading offerings…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Mail className="w-10 h-10" />}
          title="No offerings yet"
          hint="Offerings are the services, AI products, growth items, and resources we show on a Smart Invite welcome page. Add the ones your owners should be able to pick from."
          action={<button className="cc-btn primary" onClick={() => setEditing({})}>
            <Plus className="w-4 h-4" /> Add your first offering
          </button>}
        />
      ) : (
        <div className="cc-table-wrap">
          <table className="cc-table">
            <thead>
              <tr>
                <th>Section</th>
                <th>Name</th>
                <th>One-liner</th>
                <th>For</th>
                <th>Status</th>
                <th>Order</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id}>
                  <td><span className="cc-badge section">{sectionLabel(o.section)}</span></td>
                  <td><strong>{o.name}</strong></td>
                  <td className="cc-oneliner">{o.one_liner}</td>
                  <td>
                    <div className="cc-badge-row">
                      {(o.for_entity_types || []).length === 0
                        ? <span className="cc-badge any">any entity</span>
                        : o.for_entity_types.map((v) => (
                            <span key={v} className="cc-badge entity">{represLabel(v)}</span>
                          ))}
                    </div>
                    <div className="cc-badge-row">
                      {(o.for_domains || []).length === 0
                        ? <span className="cc-badge any">any domain</span>
                        : o.for_domains.map((v) => (
                            <span key={v} className="cc-badge domain">{domainLabel(v)}</span>
                          ))}
                    </div>
                  </td>
                  <td>
                    <span className={`cc-badge status ${o.status}`}>
                      {o.status === 'live' ? 'Live' : 'Planned'}
                    </span>
                  </td>
                  <td>{o.display_order}</td>
                  <td>
                    <div className="cc-row-actions">
                      <button className="cc-icon-btn" title="Edit" onClick={() => setEditing(o)}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button className="cc-icon-btn danger" title="Delete" onClick={() => remove(o)}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <OfferingEditor
          row={editing}
          options={options}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => { setEditing(null); setSuccess(msg); await load(); }}
          onError={setError}
        />
      )}
    </div>
  );
}

function OfferingEditor({ row, options, onClose, onSaved, onError }) {
  const isNew = !row.id;
  const [f, setF] = useState({
    name: row.name || '',
    one_liner: row.one_liner || '',
    section: row.section || options.sections[0]?.value || 'services',
    for_entity_types: row.for_entity_types || [],
    for_domains: row.for_domains || [],
    link_url: row.link_url || '',
    status: row.status || 'live',
    display_order: row.display_order ?? 100
  });
  const [errs, setErrs] = useState({});
  const [saving, setSaving] = useState(false);

  async function save() {
    const v = validateOffering(f);
    setErrs(v);
    if (Object.keys(v).length) return;
    setSaving(true);
    try {
      if (isNew) {
        await adminFetch('/api/admin/connect/catalog/offerings', {
          method: 'POST', body: JSON.stringify(f)
        });
        await onSaved(`Added "${f.name}".`);
      } else {
        await adminFetch(`/api/admin/connect/catalog/offerings/${row.id}`, {
          method: 'PATCH', body: JSON.stringify(f)
        });
        await onSaved(`Saved "${f.name}".`);
      }
    } catch (err) {
      onError(err.message);
      setSaving(false);
    }
  }

  return (
    <Modal title={isNew ? 'Add offering' : 'Edit offering'} onClose={onClose}>
      <Field label="Name" required error={errs.name}
             help="Short title shown on the invite page (e.g. 'WMS Implementation').">
        <input className="cc-input" value={f.name}
               onChange={(e) => setF({ ...f, name: e.target.value })}
               placeholder="e.g. WMS Implementation" />
      </Field>

      <Field label="One-liner" required error={errs.one_liner}
             help="One sentence a stranger can grasp in 5 seconds.">
        <input className="cc-input" value={f.one_liner}
               onChange={(e) => setF({ ...f, one_liner: e.target.value })}
               placeholder="e.g. End-to-end Manhattan WMi rollouts for warehousing teams." />
      </Field>

      <Field label="Link URL" required error={errs.link_url}
             help="Opens in a new tab from the invite page.">
        <div className="cc-input-with-icon">
          <LinkIcon className="w-4 h-4" />
          <input className="cc-input" value={f.link_url}
                 onChange={(e) => setF({ ...f, link_url: e.target.value })}
                 placeholder="https://skilldelivery.co.in/..." />
        </div>
      </Field>

      <Field label="Section" required error={errs.section}
             help="Which section this card appears under on the welcome page.">
        <select className="cc-input" value={f.section}
                onChange={(e) => setF({ ...f, section: e.target.value })}>
          {options.sections.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </Field>

      <Field label="For entity types"
             help="Pre-ticked when the contact's represents value matches. Leave empty (or All) to apply to every type.">
        <MultiCheck options={options.represents} value={f.for_entity_types}
                    onChange={(v) => setF({ ...f, for_entity_types: v })} />
      </Field>

      <Field label="For domains"
             help="Pre-ticked when the contact's domain matches. Leave empty (or All) to apply to every domain.">
        <MultiCheck options={options.domains} value={f.for_domains}
                    onChange={(v) => setF({ ...f, for_domains: v })} />
      </Field>

      <Field label="Status" help="Planned offerings render with a 'coming soon' badge on the invite page.">
        <select className="cc-input" value={f.status}
                onChange={(e) => setF({ ...f, status: e.target.value })}>
          {options.statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </Field>

      <Field label="Display order" help="Lower numbers appear first.">
        <input className="cc-input" type="number" value={f.display_order}
               onChange={(e) => setF({ ...f, display_order: parseInt(e.target.value, 10) || 0 })} />
      </Field>

      <div className="cc-modal-actions">
        <button className="cc-btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="cc-btn primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : (isNew ? 'Add offering' : 'Save changes')}
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Insights
// ═══════════════════════════════════════════════════════════════════════════

function InsightsPanel({ options, setDisabled, setError, setSuccess }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch('/api/admin/connect/catalog/insights');
      setRows(r.data || []);
      setDisabled(false);
    } catch (err) {
      if (err.code === 'SMART_INVITE_DISABLED') { setDisabled(true); setRows([]); }
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [setDisabled, setError]);

  useEffect(() => { load(); }, [load]);

  const domainLabel = (v) => options.domains.find(d => d.value === v)?.label || v;
  const kindLabel   = (v) => options.kinds.find(k => k.value === v)?.label || v;

  async function remove(row) {
    if (!window.confirm(`Delete "${row.title}"? This cannot be undone.`)) return;
    try {
      await adminFetch(`/api/admin/connect/catalog/insights/${row.id}`, { method: 'DELETE' });
      setSuccess(`Deleted "${row.title}".`);
      await load();
    } catch (err) { setError(err.message); }
  }

  return (
    <div className="cc-card">
      <div className="cc-card-header">
        <div className="cc-card-title">
          Insights &amp; Research <span className="cc-count">({rows.length})</span>
        </div>
        <button className="cc-btn primary" onClick={() => setEditing({})}>
          <Plus className="w-4 h-4" /> Add insight / research
        </button>
      </div>

      {loading ? (
        <div className="cc-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Mail className="w-10 h-10" />}
          title="No insights or research cards yet"
          hint="Insight cards are the market signals and deep-research pieces an owner can attach to a Smart Invite. Add a few per domain so pitches have substance from day one."
          action={<button className="cc-btn primary" onClick={() => setEditing({})}>
            <Plus className="w-4 h-4" /> Add your first card
          </button>}
        />
      ) : (
        <div className="cc-table-wrap">
          <table className="cc-table">
            <thead>
              <tr>
                <th>Kind</th>
                <th>Title</th>
                <th>One-liner</th>
                <th>Domains</th>
                <th>Source · Date</th>
                <th>Order</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id}>
                  <td><span className={`cc-badge kind ${i.kind}`}>{kindLabel(i.kind)}</span></td>
                  <td><strong>{i.title}</strong></td>
                  <td className="cc-oneliner">{i.one_liner}</td>
                  <td>
                    <div className="cc-badge-row">
                      {(i.for_domains || []).length === 0
                        ? <span className="cc-badge any">any domain</span>
                        : i.for_domains.map((v) => (
                            <span key={v} className="cc-badge domain">{domainLabel(v)}</span>
                          ))}
                    </div>
                  </td>
                  <td className="cc-meta">
                    {i.source || '—'}
                    {i.published_date ? ` · ${String(i.published_date).slice(0, 10)}` : ''}
                  </td>
                  <td>{i.display_order}</td>
                  <td>
                    <div className="cc-row-actions">
                      <button className="cc-icon-btn" title="Edit" onClick={() => setEditing(i)}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button className="cc-icon-btn danger" title="Delete" onClick={() => remove(i)}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <InsightEditor
          row={editing}
          options={options}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => { setEditing(null); setSuccess(msg); await load(); }}
          onError={setError}
        />
      )}
    </div>
  );
}

function InsightEditor({ row, options, onClose, onSaved, onError }) {
  const isNew = !row.id;
  const [f, setF] = useState({
    kind: row.kind || options.kinds[0]?.value || 'insight',
    title: row.title || '',
    one_liner: row.one_liner || '',
    link_url: row.link_url || '',
    source: row.source || '',
    published_date: row.published_date ? String(row.published_date).slice(0, 10) : '',
    for_domains: row.for_domains || [],
    display_order: row.display_order ?? 100
  });
  const [errs, setErrs] = useState({});
  const [saving, setSaving] = useState(false);

  async function save() {
    const v = validateInsight(f);
    setErrs(v);
    if (Object.keys(v).length) return;
    setSaving(true);
    try {
      const payload = { ...f, published_date: f.published_date || null };
      if (isNew) {
        await adminFetch('/api/admin/connect/catalog/insights', {
          method: 'POST', body: JSON.stringify(payload)
        });
        await onSaved(`Added "${f.title}".`);
      } else {
        await adminFetch(`/api/admin/connect/catalog/insights/${row.id}`, {
          method: 'PATCH', body: JSON.stringify(payload)
        });
        await onSaved(`Saved "${f.title}".`);
      }
    } catch (err) { onError(err.message); setSaving(false); }
  }

  return (
    <Modal title={isNew ? 'Add insight / research' : 'Edit insight / research'} onClose={onClose}>
      <Field label="Kind" required error={errs.kind}
             help="Insights are short market signals; research is a deeper piece.">
        <select className="cc-input" value={f.kind}
                onChange={(e) => setF({ ...f, kind: e.target.value })}>
          {options.kinds.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
      </Field>

      <Field label="Title" required error={errs.title}
             help="Shown on the welcome page card.">
        <input className="cc-input" value={f.title}
               onChange={(e) => setF({ ...f, title: e.target.value })}
               placeholder="e.g. India WMS market snapshot" />
      </Field>

      <Field label="One-liner" required error={errs.one_liner}
             help="One sentence that previews what the reader will find at the link.">
        <input className="cc-input" value={f.one_liner}
               onChange={(e) => setF({ ...f, one_liner: e.target.value })} />
      </Field>

      <Field label="Link URL" required error={errs.link_url}
             help="Open in a new tab from the invite page.">
        <div className="cc-input-with-icon">
          <LinkIcon className="w-4 h-4" />
          <input className="cc-input" value={f.link_url}
                 onChange={(e) => setF({ ...f, link_url: e.target.value })}
                 placeholder="https://..." />
        </div>
      </Field>

      <Field label="Source"
             help="Attribution shown as a small line under the card. e.g. 'Gartner, Oct 2026'.">
        <input className="cc-input" value={f.source}
               onChange={(e) => setF({ ...f, source: e.target.value })}
               placeholder="e.g. SkillDelivery field notes" />
      </Field>

      <Field label="Published date" help="Optional. If set, shown next to the source.">
        <input className="cc-input" type="date" value={f.published_date}
               onChange={(e) => setF({ ...f, published_date: e.target.value })} />
      </Field>

      <Field label="For domains"
             help="Pre-ticked when the contact's domain matches. Leave empty (or All) for every domain.">
        <MultiCheck options={options.domains} value={f.for_domains}
                    onChange={(v) => setF({ ...f, for_domains: v })} />
      </Field>

      <Field label="Display order" help="Lower numbers appear first.">
        <input className="cc-input" type="number" value={f.display_order}
               onChange={(e) => setF({ ...f, display_order: parseInt(e.target.value, 10) || 0 })} />
      </Field>

      <div className="cc-modal-actions">
        <button className="cc-btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="cc-btn primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : (isNew ? 'Add card' : 'Save changes')}
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UI atoms
// ═══════════════════════════════════════════════════════════════════════════

function Field({ label, required, error, help, children }) {
  return (
    <div className="cc-field">
      <label className="cc-field-label">
        {label}{required && <span className="cc-required">*</span>}
      </label>
      {children}
      {help && !error && <div className="cc-field-help">{help}</div>}
      {error && <div className="cc-field-error">{error}</div>}
    </div>
  );
}

function MultiCheck({ options, value, onChange }) {
  const allSelected = options.every((o) => value.includes(o.value));
  function toggleAll() {
    if (allSelected) onChange([]);
    else onChange(options.map((o) => o.value));
  }
  return (
    <div className="cc-multi">
      <button type="button"
              className={`cc-chip all ${allSelected ? 'on' : ''}`}
              onClick={toggleAll}
              title={allSelected ? 'Clear all' : 'Select all'}>
        {allSelected ? '✓ All' : 'All'}
      </button>
      {options.map((opt) => {
        const on = value.includes(opt.value);
        return (
          <label key={opt.value} className={`cc-chip ${on ? 'on' : ''}`}>
            <input
              type="checkbox"
              checked={on}
              onChange={() => {
                onChange(on ? value.filter(v => v !== opt.value) : [...value, opt.value]);
              }}
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  // ESC closes modal, body scroll locked while open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <div className="cc-modal-backdrop" onClick={onClose}>
      <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cc-modal-header">
          <h2>{title}</h2>
          <button className="cc-icon-btn" onClick={onClose} aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="cc-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, hint, action }) {
  return (
    <div className="cc-empty">
      <div className="cc-empty-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{hint}</p>
      {action}
    </div>
  );
}
